import { DatePipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { StateService } from '@uirouter/core';

import { CurrentPlanService } from 'src/app/components/plans/current-plan.service';

import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';

@Component({
  selector: 'plan-banner',
  templateUrl: './plan-banner.component.html',
  styleUrls: ['./plan-banner.component.scss'],
  standalone: false,
  providers: [DatePipe],
})
export class PlanBannerComponent implements OnInit, OnDestroy {
  subscription;
  protected type = signal('free-trial');
  protected title = signal('');
  protected description = signal('');
  protected badge = signal('');
  protected cta = signal('');
  protected showCtaButton = signal(true);
  protected progressValue = signal(0);
  protected daysLeft = signal(0);

  @Input() collapsed: boolean = false;

  constructor(
    private broadcaster: BroadcasterService,
    private stateService: StateService,
    private currentPlanService: CurrentPlanService,
    private datePipe: DatePipe,
  ) {
    this.plan = this.currentPlanService.currentPlan;
    this.subscription = this.broadcaster.on('risevision.plan.loaded', () => {
      this.plan = this.currentPlanService.currentPlan;
      this.setBannerType();
      this.updateCardByType(this.type());
    });
  }

  ngOnInit(): void {
    this.setBannerType();
    this.updateCardByType(this.type());
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get planTrialPeriod() {
    return (
      this.plan.trialPeriod +
      ' ' +
      (this.plan.trialPeriod === 1 ? 'day' : 'days')
    );
  }

  plan: any = {};
  showPlans = this.currentPlanService.showPurchaseOptions.bind(
    this.currentPlanService,
  );

  isEnterpriseSubCompany = this.currentPlanService.isEnterpriseSubCompany.bind(
    this.currentPlanService,
  );

  setBannerType() {
    if (this.currentPlanService.isParentPlan()) {
      this.type.set('hidden');
    } else if (
      this.currentPlanService.isPaused() &&
      this.currentPlanService.currentPlan.isPurchasedByParent
    ) {
      this.type.set('paused-contact-admin');
    } else if (this.currentPlanService.currentPlan.isPurchasedByParent) {
      this.type.set('hidden');
    } else if (this.currentPlanService.currentPlan.isAllocatedByParent) {
      this.type.set('hidden');
    } else if (this.currentPlanService.isCancelledActive()) {
      this.type.set('expires-soon');
    } else if (
      this.currentPlanService.isFree() ||
      this.currentPlanService.isCancelled()
    ) {
      this.type.set('subscribe');
    } else if (this.currentPlanService.isSubscribed()) {
      this.type.set('hidden');
    } else if (this.currentPlanService.isOnTrial()) {
      this.type.set('free-trial');
    } else if (this.currentPlanService.isTrialExpired()) {
      this.type.set('subscribe');
    } else if (this.currentPlanService.isPaused()) {
      this.type.set('billing-issue');
    } else {
      this.type.set('hidden');
    }
  }

  private updateCardByType(type: string) {
    this.title.set('');
    this.description.set('');
    this.badge.set('');
    this.cta.set('');
    this.showCtaButton.set(true);
    switch (type) {
      case 'subscribe':
        this.title.set('Get more out of Rise Vision!');
        this.description.set(
          'Explore all features before you upgrade your plan!',
        );
        this.badge.set('Subscribe');
        this.cta.set('See Plans');
        break;
      case 'free-trial':
        this.title.set('Free trial');
        this.description.set(
          'Explore all features before you upgrade your plan!',
        );
        this.cta.set('See Plans');
        this.daysLeft.set(this.plan.trialPeriod);
        this.calculateProgressValue();
        break;
      case 'expires-soon':
        const daysLeft = this.differenceInDays(
          new Date(this.plan.currentPeriodEndDate),
          new Date(),
        );

        this.title.set(
          `Expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}!`,
        );
        this.description.set(
          `Your Subscription expires on: ${this.datePipe.transform(
            this.plan.currentPeriodEndDate,
            'MMM-dd-yyyy',
          )}`,
        );
        this.badge.set('Action Needed!');
        this.cta.set('Account Settings');
        break;
      case 'billing-issue':
        this.title.set('Billing Issue');
        this.description.set(
          'There was an issue processing your payment. Please update your billing information. Your Displays may be affected.',
        );
        this.badge.set('Action Needed!');
        this.cta.set('Update Billing');
        break;
      case 'paused-contact-admin':
        this.title.set('Billing Issue');
        this.description.set(this.getPausedContactMessage());
        this.badge.set('Action Needed!');
        this.cta.set('Contact Billing Administrator');
        this.showCtaButton.set(false);
        break;
    }
  }

  private getPausedContactMessage() {
    const contact =
      this.plan.parentPlanContactEmail || this.plan.parentPlanCompanyName;

    return (
      'There was an issue processing your payment. Please contact your billing administrator' +
      (contact ? ' at ' + contact : '') +
      '. Your Displays may be affected.'
    );
  }

  protected actionClick() {
    if (['billing-issue', 'expires-soon'].includes(this.type())) {
      this.stateService.go('apps.settings.billing.home');
    } else {
      this.showPlans();
    }
  }

  private differenceInDays(date1: Date, date2: Date) {
    const difference = date1.getTime() - date2.getTime();
    return Math.ceil(difference / (1000 * 60 * 60 * 24));
  }

  private calculateProgressValue() {
    const { trialPeriod } = this.plan;

    if (trialPeriod > 14) this.progressValue.set(100);
    else this.progressValue.set(Math.round((trialPeriod / 14) * 100));
  }
}
